import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography, alpha, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ProposalProcessStatus } from '../../../../application/common/enums/proposal-process-status';
import { Close } from '@mui/icons-material';
export function DeleteDialog({ open, proposalName, proposals, onClose, onDelete, onRemoveItem }) {
    const { t } = useTranslation();
    const theme = useTheme();
    // Helper to render status chips (only used in multiple delete view)
    const getStatusChip = (status) => {
        let color = theme.palette.info.light;
        let label = 'Unknown';
        let textColor = theme.palette.info.main;
        let bgColor = alpha(theme.palette.info.main, 0.1);
        // Convert to unknown then number to avoid strict enum overlap errors if types are mismatched
        const statusVal = status;
        if (status === ProposalProcessStatus.DocumentProcessed ||
            status === 'DocumentProcessed' ||
            statusVal === 1) {
            label = 'Available';
            textColor = theme.palette.success.main;
            bgColor = alpha(theme.palette.success.main, 0.1);
        }
        else if (status === ProposalProcessStatus.DocumentProcessing ||
            status === 'DocumentProcessing' ||
            statusVal === 0) {
            label = 'Loading';
            textColor = theme.palette.warning.main;
            bgColor = alpha(theme.palette.warning.main, 0.1);
        }
        else if (status === ProposalProcessStatus.Failed ||
            status === 'Failed' ||
            statusVal === 2) {
            label = 'Error';
            textColor = theme.palette.error.main;
            bgColor = alpha(theme.palette.error.main, 0.1);
        }
        return (_jsx(Chip, { label: label, size: 'small', sx: {
                backgroundColor: bgColor,
                color: textColor,
                borderRadius: 3,
            } }));
    };
    // If we have a list of proposals (Advanced UI for Proposals page)
    if (proposals && proposals.length > 0) {
        return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: 'sm', fullWidth: true, PaperProps: {
                sx: {
                    borderRadius: '24px',
                    padding: '16px'
                }
            }, children: [_jsx(DialogTitle, { sx: { p: 1, mb: 2 }, children: _jsxs(Box, { sx: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }, children: [_jsx(Typography, { variant: 'h6', fontWeight: 700, children: t('deleteProposals') || 'Delete Proposals?' }), _jsx(IconButton, { size: 'small', onClick: onClose, children: _jsx(Close, { fontSize: 'small' }) })] }) }), _jsxs(DialogContent, { sx: { p: 0 }, children: [_jsxs(Box, { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 1, children: [_jsx(Typography, { variant: 'subtitle1', fontWeight: 700, gutterBottom: true, children: t('deleteConfirmationTitle') || "You're about to delete the selected proposals." }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('deleteConfirmationSubtitle') || 'This action cannot be undone.' })] }), _jsx(Box, { sx: { maxHeight: '300px', overflowY: 'auto', px: 1 }, children: proposals.map((proposal) => (_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1,
                                    mb: 1,
                                    borderRadius: '16px',
                                    boxShadow: '0px 12px 24px -4px #919EAB1F , 0px 0px 2px 0px #919EAB33',
                                    bgcolor: 'white'
                                }, children: [_jsxs(Box, { children: [_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, children: [_jsx(Typography, { variant: 'subtitle2', children: proposal.projectName }), getStatusChip(proposal.documentsProcess)] }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', children: proposal.companyName || proposal.projectCategory || 'Unknown Category' })] }), onRemoveItem && proposals.length > 1 && (_jsx(IconButton, { size: 'small', sx: { backgroundColor: alpha(theme.palette.grey[500], 0.12) }, onClick: () => onRemoveItem(proposal.id), children: _jsx(Close, { fontSize: 'small' }) }))] }, proposal.id))) })] }), _jsxs(DialogActions, { sx: { p: 2 }, children: [_jsx(Button, { variant: 'contained', color: 'primary', size: 'large', onClick: onDelete, children: t('delete') }), _jsx(Button, { variant: 'outlined', onClick: onClose, size: 'large', children: t('close') })] })] }));
    }
    // Fallback for simple single delete (Legacy/KnowledgeBase compatible)
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: 'sm', fullWidth: true, children: [_jsx(DialogTitle, { children: t('deleteConfirmation') }), _jsx(DialogContent, { children: _jsxs(Typography, { children: [t('areYouSureYouWantToDelete'), " ", _jsx("strong", { children: proposalName }), " ?"] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { style: { backgroundColor: 'red', color: 'white' }, onClick: onDelete, children: t('delete') }), _jsx(Button, { onClick: onClose, children: t('no') })] })] }));
}
